/**
 * Kalıcı sol kenar çubuğu — uygulamanın omurgası.
 *
 * Ekranlar `config/navigation.jsx`'teki gruplara göre listelenir ve
 * kullanıcının rolüne göre süzülür; böylece kimin neyi gördüğü eski üst
 * menüyle bire bir aynı kalır.
 *
 * Daraltıldığında yalnız ikonlar kalır, etiketler tooltip'e düşer.
 */
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { Tooltip } from "antd";
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import bshLogo from "../../assets/image/logo.png";
import smartLogo from "../../assets/image/smartoptLogo.png";
import smartTabLogo from "../../assets/image/smartoptTabLogo.png";
import { filterNavForRoles, isItemActive } from "../../config/navigation";
import UserContext from "../../Context/UserContext";

const NavLinkItem = ({ item, collapsed, onNavigate }) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const active = isItemActive(item, pathname);

  const link = (
    <Link
      to={item.path}
      onClick={onNavigate}
      aria-current={active ? "page" : undefined}
      className={[
        "group relative flex items-center rounded-lg transition-colors duration-150",
        collapsed ? "h-10 w-10 justify-center" : "h-9 gap-3 px-3",
        active
          ? "bg-ink-700 text-white"
          : "text-ink-300 hover:bg-ink-800 hover:text-white",
      ].join(" ")}
    >
      {/* Aktif göstergesi: sol kenarda sarı çubuk */}
      <span
        className={[
          "absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-brand-400 transition-opacity",
          active ? "opacity-100" : "opacity-0",
        ].join(" ")}
      />
      <span
        className={[
          "flex shrink-0 items-center justify-center text-[16px] transition-colors",
          active ? "text-brand-400" : "text-ink-300 group-hover:text-white",
        ].join(" ")}
      >
        {item.icon}
      </span>
      {!collapsed && (
        <>
          <span className="truncate text-[13.5px] font-medium">
            {t(item.label)}
          </span>
          {item.badge && (
            <span className="ml-auto rounded bg-brand-400/15 px-1.5 py-0.5 text-2xs font-semibold uppercase tracking-label text-brand-300">
              {item.badge}
            </span>
          )}
        </>
      )}
    </Link>
  );

  return collapsed ? (
    <Tooltip title={t(item.label)} placement="right">
      {link}
    </Tooltip>
  ) : (
    link
  );
};

const Sidebar = ({
  collapsed = false,
  onToggle,
  onNavigate,
  showToggle = true,
}) => {
  const { t } = useTranslation();
  const { user } = useContext(UserContext);

  const roles = user?.accountDetail?.idTokenClaims?.roles || [];
  // roles referansı her render'da yeni dizi olabildiği için içeriğe göre anahtarla.
  const sections = useMemo(
    () => filterNavForRoles(roles),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [roles.join("|")]
  );

  const isAdmin = roles.includes("ps_admin");

  return (
    <aside
      className={[
        "flex h-full flex-col bg-ink-900 text-white",
        collapsed ? "items-center" : "",
      ].join(" ")}
    >
      {/* --- Marka --- */}
      <div
        className={[
          "flex h-16 shrink-0 items-center border-b border-white/[0.07]",
          collapsed ? "w-full justify-center px-2" : "w-full gap-3 px-4",
        ].join(" ")}
      >
        {/* Beyaz kutu YOK: bu logo.png beyaz bir BSH logosu (eski koyu
            navbar için çizilmiş). Beyaz zemine konunca görünmez oluyordu;
            koyu kenar çubuğunun üstünde doğrudan durması gerekiyor. */}
        <div
          className={[
            "flex shrink-0 items-center",
            // Görselin üst/altında geniş saydam boşluk var; kutu başlığın
            // tam yüksekliğini (h-16) alsa da logo kenarlara değmez.
            collapsed ? "h-12 w-12" : "h-16 w-auto max-w-[185px]",
          ].join(" ")}
        >
          <img
            src={bshLogo}
            alt="BSH"
            // Kutu zaten başlık yüksekliğinde; büyütme ölçekle yapılır.
            className={[
              "max-h-full max-w-full object-contain scale-[1.15]",
              collapsed ? "origin-center" : "origin-left",
            ].join(" ")}
          />
        </div>
        {showToggle && !collapsed && (
          <button
            type="button"
            onClick={onToggle}
            aria-label={t("shell.collapse")}
            className="ml-auto flex h-7 w-7 items-center justify-center rounded-md text-ink-300 transition-colors hover:bg-ink-700 hover:text-white"
          >
            <MenuFoldOutlined />
          </button>
        )}
      </div>

      {showToggle && collapsed && (
        <button
          type="button"
          onClick={onToggle}
          aria-label={t("shell.expand")}
          className="mt-3 flex h-8 w-8 items-center justify-center rounded-md text-ink-300 transition-colors hover:bg-ink-700 hover:text-white"
        >
          <MenuUnfoldOutlined />
        </button>
      )}

      {/* --- Gruplanmış gezinme --- */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-4">
        {sections.map((section, index) => (
          <div key={section.key} className={index === 0 ? "" : "mt-5"}>
            {collapsed ? (
              index > 0 && <div className="mx-auto mb-3 h-px w-6 bg-white/10" />
            ) : (
              <div className="mb-1.5 px-3 text-2xs font-semibold uppercase tracking-label text-ink-400">
                {t(section.label)}
              </div>
            )}
            <div
              className={
                collapsed
                  ? "flex flex-col items-center gap-1"
                  : "flex flex-col gap-0.5"
              }
            >
              {section.items.map((item) => (
                <NavLinkItem
                  key={item.key}
                  item={item}
                  collapsed={collapsed}
                  onNavigate={onNavigate}
                />
              ))}
            </div>
          </div>
        ))}

        {/* Admin paneli — eski üst menüde de yalnız ps_admin görüyordu. */}
        {isAdmin && (
          <div className="mt-5">
            {!collapsed && (
              <div className="mb-1.5 px-3 text-2xs font-semibold uppercase tracking-label text-ink-400">
                {t("nav.groups.admin")}
              </div>
            )}
            <a
              href="https://bsh.api-boyahane.smartopt.com.tr/admin/"
              target="_blank"
              rel="noreferrer"
              className={[
                "flex items-center rounded-lg text-ink-300 transition-colors hover:bg-ink-800 hover:text-white",
                collapsed ? "mx-auto h-10 w-10 justify-center" : "h-9 gap-3 px-3",
              ].join(" ")}
            >
              <RightOutlined className="text-[13px]" />
              {!collapsed && (
                <span className="truncate text-[13.5px] font-medium">
                  {t("nav.items.adminPanel")}
                </span>
              )}
            </a>
          </div>
        )}
      </nav>

      {/* --- Alt bilgi --- */}
      <div
        className={[
          "shrink-0 border-t border-white/[0.07]",
          collapsed ? "w-full px-2 py-3" : "w-full px-4 py-3.5",
        ].join(" ")}
      >
        {collapsed ? (
          <Tooltip title="SmartOpt" placement="right">
            {/* Beyaz kutu şart: logonun "M" harfi neredeyse siyah, koyu
                kenar çubuğunda doğrudan konunca kayboluyor. */}
            <div className="mx-auto flex h-7 w-7 items-center justify-center rounded-md bg-white p-1">
              <img
                src={smartTabLogo}
                alt="SmartOpt"
                className="max-h-full max-w-full object-contain"
              />
            </div>
          </Tooltip>
        ) : (
          <div className="flex items-center justify-between gap-2">
            <img
              src={smartLogo}
              alt="SmartOpt"
              className="h-5 w-auto opacity-80"
            />
            <a
              href="https://smartopt.com.tr"
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1 text-2xs text-ink-400 transition-colors hover:text-brand-300"
            >
              {t("shell.poweredBy")}
              <RightOutlined className="text-[8px]" />
            </a>
          </div>
        )}
      </div>
    </aside>
  );
};

export default Sidebar;
